import * as React from "react";
import { AlertTriangle, Info } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DiscoveryCycle,
  DiscoveryCycleCensusState,
  DiscoveryCycleCoverage,
  hasCensusFigures,
} from "../../models/Statistics/DiscoveryCycle";
import { MigrationType } from "../../models/Statistics/MigrationType";
import ConfiguredSourceRootsModal from "./ConfiguredSourceRootsModal";
import CoverageBadge from "./CoverageBadge";

/**
 * What to say about a cycle carrying no figures. The three causes are indistinguishable by the counters alone
 * and mean different things, so each gets its own sentence: a client that does not report links at all, a cycle
 * the server closed before its counters were sent, and a client that counted and then withheld.
 */
const NoFiguresMessage: Record<string, string> = {
  [DiscoveryCycleCensusState.NotReported]:
    "No link data. This cycle ran on a client that does not report source links.",
  [DiscoveryCycleCensusState.ClosedByServer]:
    "This cycle never reported an end. The server closed it when the client started the next one, so its counters were never sent.",
  [DiscoveryCycleCensusState.Withheld]:
    "This client uploaded the problems it found but not the totals, so there is no way to check nothing was lost on the way.",
};

const numberFormat = new Intl.NumberFormat();

/**
 * One figure from the census. An absent value renders as a dash, never as zero: a client running without a cache
 * cannot count the whole scan, and "0 links scanned" would describe a cycle that did nothing.
 */
const Figure: React.FC<{ label: string; value?: number; muted?: boolean }> = ({ label, value, muted }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xs text-muted-foreground">{label}</span>
    {value === undefined || value === null ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-sm text-muted-foreground cursor-default">&mdash;</span>
        </TooltipTrigger>
        <TooltipContent>This cycle did not report this figure.</TooltipContent>
      </Tooltip>
    ) : (
      <span className={`text-sm tabular-nums ${muted ? "text-muted-foreground" : "font-medium"}`}>
        {numberFormat.format(value)}
      </span>
    )}
  </div>
);

/**
 * A titled band of figures. The heading carries the unit, which is what stops a link count being read as a file
 * count: one configured folder link can stand for a whole tree, so the two bands differ by orders of magnitude
 * and nothing in the numbers themselves says which is which.
 */
const FigureGroup: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="flex flex-col gap-1.5">
    <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">{title}</span>
    <div className="flex flex-wrap gap-x-8 gap-y-3">{children}</div>
  </div>
);

const plural = (count: number, singular: string) =>
  `${numberFormat.format(count)} ${count === 1 ? singular : `${singular}s`}`;

/** Sums only when every input is present — a partial sum would invent a total nobody reported. */
const sumIfAllKnown = (values: (number | undefined)[]): number | undefined =>
  values.some((value) => value === undefined || value === null)
    ? undefined
    : values.reduce((total, value) => (total ?? 0) + (value ?? 0), 0);

/**
 * The scan summary for one discovery cycle. These counters are the only census there is — the registry holds a
 * row per link a client chose to report, not one per link scanned — so they are reconciled rather than merely
 * displayed, which is what catches a client that miscounted or an upload cut short.
 */
export const DiscoveryCycleCensus: React.FC<{
  cycle: DiscoveryCycle;
  organizationId: string;
  migrationType: MigrationType;
}> = ({ cycle, organizationId, migrationType }) => {
  const [rootsOpen, setRootsOpen] = React.useState(false);

  // The count is the client's tally and names nothing, so it is a way in to the registry rather than a figure.
  // A plain onClick rather than a DialogTrigger: the trigger has to be the DOM element that receives the click.
  const rootsFigure = cycle.rootsUnreachable > 0 && (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">Roots unreachable</span>
      <Button
        variant="link"
        className="h-auto w-fit justify-start p-0 text-sm font-medium tabular-nums"
        onClick={() => setRootsOpen(true)}
      >
        {numberFormat.format(cycle.rootsUnreachable)}
      </Button>
    </div>
  );

  const rootsModal = (
    <ConfiguredSourceRootsModal
      cycle={cycle}
      organizationId={organizationId}
      open={rootsOpen}
      onClose={() => setRootsOpen(false)}
    />
  );

  if (!hasCensusFigures(cycle)) {
    const withheld = cycle.censusState === DiscoveryCycleCensusState.Withheld;
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-start gap-2 text-sm text-muted-foreground">
          {withheld ? (
            <AlertTriangle className="size-4 shrink-0 text-destructive" />
          ) : (
            <Info className="size-4 shrink-0" />
          )}
          <span>{NoFiguresMessage[cycle.censusState] ?? NoFiguresMessage[DiscoveryCycleCensusState.NotReported]}</span>
        </div>

        {/* Worth stating on this branch only: with no buckets there is nothing to reconcile the uploaded rows
            against, so the check that would catch a dropped upload is off for as long as this persists. */}
        {withheld && (
          <div className="flex flex-wrap gap-x-8 gap-y-3">
            <Figure label="Sent" value={cycle.exceptionRowsReported} />
          </div>
        )}

        {/* Roots are reported before the entity stream and separately from links, so a cycle with no census at
            all can still carry an outage — the one thing on such a cycle worth reading. */}
        {rootsFigure && <div className="flex flex-wrap gap-x-8 gap-y-3">{rootsFigure}</div>}
        {rootsModal}
      </div>
    );
  }

  const exceptionBuckets = sumIfAllKnown([
    cycle.linksNothingDiscovered,
    cycle.linksPartiallyDiscovered,
    cycle.linksExcluded,
    cycle.linksDuplicateTarget,
  ]);

  const censusTotal = sumIfAllKnown([
    cycle.linksFilesDiscovered,
    cycle.linksTargetEmpty,
    cycle.linksPartiallyDiscovered,
    cycle.linksNothingDiscovered,
    cycle.linksExcluded,
    cycle.linksDuplicateTarget,
  ]);

  const hasScanTotal = cycle.linksScanned !== undefined && cycle.linksScanned !== null;

  // Signed: outcomes exceeding the scan total is a different mistake from links never being given one, and one
  // label cannot describe both.
  const censusGap =
    hasScanTotal && censusTotal !== undefined ? cycle.linksScanned! - censusTotal : undefined;

  // Checkable without a map of the whole scan: the buckets are what the client found, and the four terms are
  // what became of each one. Mirrors DiscoveryCycle.EvaluateCensusConsistency — a link withheld beneath a downed
  // root, lost with a failed report, or deliberately not re-sent was still found, so each closes the identity
  // rather than leaving a residual nobody can attribute. Signed like censusGap: a dropped upload and a wrong
  // tally are different faults.
  const accountedFor =
    cycle.exceptionRowsReported +
    (cycle.linksSuppressed ?? 0) +
    (cycle.linksUnreported ?? 0) +
    (cycle.linksUnchanged ?? 0);

  const uploadGap = exceptionBuckets !== undefined ? exceptionBuckets - accountedFor : undefined;

  // Rendered only when the client sent some of it. A build predating file reporting would otherwise show a band
  // of seven dashes, which reads as a scan that found nothing rather than one nobody counted this way.
  const hasFileCensus = [
    cycle.filesProduced,
    cycle.filesUploadable,
    cycle.filesBlocked,
    cycle.filesUnmodified,
    cycle.filesFiltered,
    cycle.filesDuplicate,
    cycle.filesErrored,
  ].some((value) => value !== undefined && value !== null);

  // Blocked and unmodified files belong on this side of the sum: they were discovered, and are merely withheld
  // from upload or already current. Only filtered, duplicate and errored never became files.
  const fileParts = sumIfAllKnown([
    cycle.filesUploadable,
    cycle.filesBlocked,
    cycle.filesUnmodified,
    cycle.filesFiltered,
    cycle.filesDuplicate,
    cycle.filesErrored,
  ]);

  const hasFilesTotal = cycle.filesProduced !== undefined && cycle.filesProduced !== null;

  // Signed like the two above, and for the same reason.
  const fileGap =
    hasFilesTotal && fileParts !== undefined ? cycle.filesProduced! - fileParts : undefined;

  // Unproven says nothing of its own; it shows only as "Degraded", and only when the client noticed something.
  const hasVerdict =
    cycle.coverage === DiscoveryCycleCoverage.Complete ||
    cycle.coverage === DiscoveryCycleCoverage.Bounded ||
    cycle.coverage === DiscoveryCycleCoverage.Unbounded ||
    (cycle.coverage === DiscoveryCycleCoverage.Unproven && cycle.degraded);

  const hasBadges =
    (censusGap !== undefined && censusGap !== 0) ||
    cycle.censusState === DiscoveryCycleCensusState.WithoutScanTotal ||
    (uploadGap !== undefined && uploadGap !== 0) ||
    (fileGap !== undefined && fileGap !== 0) ||
    hasVerdict ||
    cycle.reportsPending;

  return (
    <div className="flex flex-col gap-3">
      {hasBadges && (
      <div className="flex flex-wrap items-center gap-2">
        {/* The drain handshake. A pending cycle has ended but its link reports are still landing, so nothing
            has been retired on its account yet; an incomplete drain dropped an entity and blocks the sweep. */}
        {cycle.reportsPending && !cycle.drainedOn && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="neutral">
                <Info className="size-3" />
                Reports pending
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              Still receiving results from this cycle. Nothing is cleared out until they all arrive.
            </TooltipContent>
          </Tooltip>
        )}
        {cycle.reportsPending && cycle.drainComplete === false && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="warning">
                <AlertTriangle className="size-3" />
                Reports incomplete
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              Some results never arrived, so nothing was cleared out. The client will resend them on the next cycle.
            </TooltipContent>
          </Tooltip>
        )}
        {censusGap !== undefined && censusGap !== 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="danger">
                <AlertTriangle className="size-3" />
                {censusGap > 0
                  ? `${plural(censusGap, "link")} unaccounted`
                  : `${plural(-censusGap, "outcome")} over count`}
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              The client reported {numberFormat.format(cycle.linksScanned!)} links checked but gave outcomes for{" "}
              {numberFormat.format(censusTotal!)}.
            </TooltipContent>
          </Tooltip>
        )}

        {cycle.censusState === DiscoveryCycleCensusState.WithoutScanTotal && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="neutral">
                <Info className="size-3" />
                No total
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              This cycle reported what it found but never totalled the links it checked, so the figures below cannot
              be reconciled against a total. Usual for a client holding no map of the whole source. What it found is
              still recorded.
            </TooltipContent>
          </Tooltip>
        )}

        {uploadGap !== undefined && uploadGap !== 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="warning">
                <AlertTriangle className="size-3" />
                {uploadGap > 0
                  ? `${plural(uploadGap, "link")} not sent`
                  : `${plural(-uploadGap, "link")} over the tally`}
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              {uploadGap > 0
                ? `The client found ${numberFormat.format(exceptionBuckets!)} outstanding links and accounted for ${numberFormat.format(accountedFor)} of them — sent, unchanged, held back or lost.`
                : `The client accounted for ${numberFormat.format(accountedFor)} links but its outcomes only add up to ${numberFormat.format(exceptionBuckets!)}, which should be impossible.`}
            </TooltipContent>
          </Tooltip>
        )}

        {fileGap !== undefined && fileGap !== 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="danger">
                <AlertTriangle className="size-3" />
                {fileGap > 0
                  ? `${plural(fileGap, "file")} unaccounted`
                  : `${plural(-fileGap, "file")} over count`}
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              The client reported {numberFormat.format(cycle.filesProduced!)} files produced but accounted for{" "}
              {numberFormat.format(fileParts!)} of them across uploadable, blocked, unmodified, filtered,
              duplicate and errored.
            </TooltipContent>
          </Tooltip>
        )}

        <CoverageBadge
          coverage={cycle.coverage}
          degraded={cycle.degraded}
          migrationType={migrationType}
          rootsUnreachable={cycle.rootsUnreachable}
        />
      </div>
      )}

      <FigureGroup title="Links">
        <Figure label="Checked" value={cycle.linksScanned} />
        <Figure label="Files found" value={cycle.linksFilesDiscovered} />
        <Figure label="Empty" value={cycle.linksTargetEmpty} muted />
        <Figure label="Partly found" value={cycle.linksPartiallyDiscovered} />
        <Figure label="Nothing found" value={cycle.linksNothingDiscovered} />
        <Figure label="Excluded" value={cycle.linksExcluded} muted />
        <Figure label="Duplicate" value={cycle.linksDuplicateTarget} muted />
        <Figure label="Outstanding" value={exceptionBuckets} />
        <Figure label="Sent" value={cycle.exceptionRowsReported} />
        {/* Rendered only when the client can measure them: a build that cannot would otherwise show two
            permanent dashes, which reads as a gap rather than as a question nobody asked. */}
        {(cycle.linksSuppressed !== undefined ||
          cycle.linksUnreported !== undefined ||
          cycle.linksUnchanged !== undefined) && (
          <>
            <Figure label="Unchanged" value={cycle.linksUnchanged} muted />
            <Figure label="Held back" value={cycle.linksSuppressed} muted />
            <Figure label="Report lost" value={cycle.linksUnreported} />
          </>
        )}
      </FigureGroup>

      {hasFileCensus && (
        <FigureGroup title="Files">
          <Figure label="Produced" value={cycle.filesProduced} />
          <Figure label="Uploadable" value={cycle.filesUploadable} />
          <Figure label="Blocked" value={cycle.filesBlocked} muted />
          <Figure label="Unmodified" value={cycle.filesUnmodified} muted />
          <Figure label="Filtered" value={cycle.filesFiltered} muted />
          <Figure label="Duplicate" value={cycle.filesDuplicate} muted />
          <Figure label="Errored" value={cycle.filesErrored} />
        </FigureGroup>
      )}

      {/* Outside both bands: a root yields entities, so it counts neither links nor files. */}
      {rootsFigure && <div className="flex flex-wrap gap-x-8 gap-y-3">{rootsFigure}</div>}

      {rootsModal}
    </div>
  );
};

export default DiscoveryCycleCensus;
